import types from '../../mutation-types';
import getters, { getSelectedChatConversation } from './getters';
import actions from './actions';
import { findPendingMessageIndex } from './helpers';
import { MESSAGE_STATUS } from 'shared/constants/messages';
import { CONTENT_TYPES } from 'dashboard/components-next/message/constants.js';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from '../../../../shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

// Debounce sidebar unread count refreshes to avoid flooding the UI
// when many messages arrive in rapid succession (e.g. from bots).
const SIDEBAR_REFRESH_DEBOUNCE_MS = 2000;
let _sidebarRefreshTimer = null;
const debouncedSidebarRefresh = () => {
  if (_sidebarRefreshTimer) clearTimeout(_sidebarRefreshTimer);
  _sidebarRefreshTimer = setTimeout(() => {
    emitter.emit(BUS_EVENTS.REFRESH_SIDEBAR_UNREAD_COUNTS);
    _sidebarRefreshTimer = null;
  }, SIDEBAR_REFRESH_DEBOUNCE_MS);
};

const getConversationById = _state => conversationId => {
  return _state.allConversations.find(c => c.id === conversationId);
};

const state = {
  allConversations: [],
  attachments: {},
  listLoadingStatus: true,
  chatStatusFilter: wootConstants.STATUS_TYPE.OPEN,
  chatSortFilter: wootConstants.SORT_BY_TYPE.LATEST,
  currentInbox: null,
  selectedChatId: null,
  appliedFilters: [],
  contextMenuChatId: null,
  conversationParticipants: [],
  conversationLastSeen: null,
  syncConversationsMessages: {},
  conversationFilters: {},
  copilotAssistant: {},
};

// mutations
export const mutations = {
  [types.SET_ALL_CONVERSATION](_state, payload) {
    // Two payload shapes are supported:
    //   - Array (legacy)            → merge mode (the original Chatwoot behavior)
    //   - { conversations, replace } → replace mode when `replace: true`
    //
    // Replace mode is used by fresh fetches (initial load, filter change,
    // post-mark_all_read refresh) so stale conversations from previous fetches
    // are evicted instead of accumulating in `allConversations`. Without it,
    // the unread-tab list keeps showing items the server has since marked as
    // read, while the sidebar counter shows the correct lower number — a
    // long-standing inconsistency that frustrated agents.
    //
    // Pagination + websocket reconnect deltas keep using merge mode (no
    // `replace` flag) to preserve already-loaded items.
    const isObjectPayload = payload && !Array.isArray(payload);
    const conversationList = isObjectPayload
      ? payload.conversations || []
      : payload;
    const replaceMode = isObjectPayload && payload.replace === true;

    if (replaceMode) {
      // Always preserve the active chat (the one the agent is currently
      // viewing). Without this preservation:
      //   - syncActiveConversationMessages bails (it requires the conversation
      //     in `allConversations`) → no realtime sync mid-action.
      //   - draft message + scroll position vanish from the UI.
      //
      // If the active chat is also in the new list, prefer the existing
      // in-memory copy so we don't drop messages/attachments/dataFetched.
      const activeId = _state.selectedChatId;
      const existingActive = activeId
        ? _state.allConversations.find(c => c.id === activeId)
        : null;
      const activeIdInList =
        activeId && conversationList.some(c => c.id === activeId);

      const merged = conversationList.map(c => {
        if (existingActive && c.id === existingActive.id) {
          // Same conversation, fresh server data — keep agent's local state.
          return {
            ...c,
            allMessagesLoaded: existingActive.allMessagesLoaded,
            messages: existingActive.messages,
            dataFetched: existingActive.dataFetched,
          };
        }
        return c;
      });

      if (existingActive && !activeIdInList) {
        // Active chat is no longer in the new fetch (e.g., user marked it as
        // read or filter excluded it). Prepend it so the chat panel keeps
        // working until the agent navigates away.
        _state.allConversations = [existingActive, ...merged];
      } else {
        _state.allConversations = merged;
      }
      return;
    }

    // ---------- Legacy merge mode (unchanged) ----------
    const newAllConversations = [..._state.allConversations];
    conversationList.forEach(conversation => {
      const indexInCurrentList = newAllConversations.findIndex(
        c => c.id === conversation.id
      );
      if (indexInCurrentList < 0) {
        newAllConversations.push(conversation);
      } else if (conversation.id !== _state.selectedChatId) {
        // If the conversation is already in the list, replace it
        // Added this to fix the issue of the conversation not being updated
        // When reconnecting to the websocket. If the selectedChatId is not the same as
        // the conversation.id in the store, replace the existing conversation with the new one
        newAllConversations[indexInCurrentList] = conversation;
      } else {
        // If the conversation is already in the list and selectedChatId is the same,
        // replace all data except the messages array, attachments, dataFetched, allMessagesLoaded
        const existingConversation = newAllConversations[indexInCurrentList];
        newAllConversations[indexInCurrentList] = {
          ...conversation,
          allMessagesLoaded: existingConversation.allMessagesLoaded,
          messages: existingConversation.messages,
          dataFetched: existingConversation.dataFetched,
        };
      }
    });
    _state.allConversations = newAllConversations;
  },
  [types.EMPTY_ALL_CONVERSATION](_state) {
    _state.allConversations = [];
    _state.selectedChatId = null;
  },
  [types.SET_ALL_MESSAGES_LOADED](_state) {
    const [chat] = getSelectedChatConversation(_state);
    chat.allMessagesLoaded = true;
  },

  [types.CLEAR_ALL_MESSAGES_LOADED](_state) {
    const [chat] = getSelectedChatConversation(_state);
    chat.allMessagesLoaded = false;
  },
  [types.CLEAR_CURRENT_CHAT_WINDOW](_state) {
    _state.selectedChatId = null;
  },

  [types.SET_PREVIOUS_CONVERSATIONS](_state, { id, data }) {
    if (data.length) {
      const [chat] = _state.allConversations.filter(c => c.id === id);
      if (!chat) return;
      // A page of history can overlap what the store already holds: a realtime
      // event may have appended a message that this page also contains. Without
      // this filter the same message is rendered twice, once here at the top and
      // once wherever it was appended.
      const known = new Set(chat.messages.map(m => m.id));
      const fresh = data.filter(m => !known.has(m.id));
      if (fresh.length) {
        chat.messages.unshift(...fresh);
      }
    }
  },
  [types.SET_ALL_ATTACHMENTS](_state, { id, data }) {
    _state.attachments[id] = [...data];
  },
  [types.SET_MISSING_MESSAGES](_state, { id, data }) {
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (!chat) return;
    chat.messages = data;
  },

  [types.SET_CURRENT_CHAT_WINDOW](_state, activeChat) {
    if (activeChat) {
      _state.selectedChatId = activeChat.id;
    }
  },

  [types.ASSIGN_AGENT](_state, assignee) {
    const [chat] = getSelectedChatConversation(_state);
    chat.meta.assignee = assignee;
  },

  [types.ASSIGN_TEAM](_state, { team, conversationId }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    chat.meta.team = team;
  },

  [types.ASSIGN_ISSUE](_state, { issue, conversationId }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.issue = issue;
    }
  },

  [types.UPDATE_CONVERSATION_LAST_ACTIVITY](
    _state,
    { lastActivityAt, conversationId }
  ) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      const lastActivitySeconds = Number(lastActivityAt);

      if (Number.isNaN(lastActivitySeconds)) {
        return;
      }

      const normalizedTimestamp = Math.floor(lastActivitySeconds);

      chat.last_activity_at = normalizedTimestamp;
      chat.timestamp = normalizedTimestamp;
    }
  },
  [types.ASSIGN_PRIORITY](_state, { priority, conversationId }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    chat.priority = priority;
  },

  [types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES](_state, custom_attributes) {
    const [chat] = getSelectedChatConversation(_state);
    chat.custom_attributes = custom_attributes;
  },

  [types.CHANGE_CONVERSATION_STATUS](
    _state,
    { conversationId, status, snoozedUntil }
  ) {
    const conversation =
      getters.getConversationById(_state)(conversationId) || {};
    conversation.snoozed_until = snoozedUntil;
    conversation.status = status;
  },

  [types.MUTE_CONVERSATION](_state) {
    const [chat] = getSelectedChatConversation(_state);
    chat.muted = true;
  },

  [types.UNMUTE_CONVERSATION](_state) {
    const [chat] = getSelectedChatConversation(_state);
    chat.muted = false;
  },

  [types.ADD_CONVERSATION_ATTACHMENTS](_state, message) {
    // early return if the message has not been sent, or has no attachments
    if (
      message.status !== MESSAGE_STATUS.SENT ||
      !message.attachments?.length
    ) {
      return;
    }

    const id = message.conversation_id;
    const existingAttachments = _state.attachments[id] || [];

    const attachmentsToAdd = message.attachments.filter(attachment => {
      // if the attachment is not already in the store, add it
      // this is to prevent duplicates
      return !existingAttachments.some(
        existingAttachment => existingAttachment.id === attachment.id
      );
    });

    // replace the attachments in the store
    _state.attachments[id] = [...existingAttachments, ...attachmentsToAdd];
  },

  [types.DELETE_CONVERSATION_ATTACHMENTS](_state, message) {
    if (message.status !== MESSAGE_STATUS.SENT) return;

    const { conversation_id: id } = message;
    const existingAttachments = _state.attachments[id] || [];
    if (!existingAttachments.length) return;

    _state.attachments[id] = existingAttachments.filter(attachment => {
      return attachment.message_id !== message.id;
    });
  },

  [types.ADD_MESSAGE]({ allConversations, selectedChatId }, message) {
    const { conversation_id: conversationId } = message;
    let [chat] = getSelectedChatConversation({
      allConversations,
      selectedChatId: conversationId,
    });

    // If the conversation isn't in the store yet, create a minimal placeholder so we don't
    // drop incoming/outgoing messages (common during bursts or when the list is paginated).
    // This placeholder will be replaced/merged when the full conversation payload arrives.
    if (!chat) {
      const inboxId = message.inbox_id ? Number(message.inbox_id) : null;
      const conversationMeta = message.conversation || {};
      const lastActivityAt =
        Number(conversationMeta.last_activity_at) ||
        Number(message.created_at) ||
        Math.floor(Date.now() / 1000);

      const unreadCount =
        conversationMeta.unread_count !== undefined
          ? Number(conversationMeta.unread_count) || 0
          : 0;

      const assigneeId =
        conversationMeta.assignee_id !== undefined
          ? Number(conversationMeta.assignee_id) || null
          : null;

      const sender = message.sender || {};

      chat = {
        id: conversationId,
        inbox_id: inboxId,
        status: 'open',
        labels: [],
        meta: {
          sender,
          assignee: assigneeId ? { id: assigneeId } : null,
          team: null,
        },
        messages: [],
        unread_count: unreadCount,
        last_activity_at: lastActivityAt,
        timestamp: lastActivityAt,
        created_at: lastActivityAt,
        updated_at: lastActivityAt,
      };

      allConversations.push(chat);
    }

    const pendingMessageIndex = findPendingMessageIndex(chat, message);
    if (pendingMessageIndex !== -1) {
      chat.messages[pendingMessageIndex] = message;
    } else {
      chat.messages.push(message);
      chat.timestamp = message.created_at;
      const { conversation: { unread_count: unreadCount = 0 } = {} } = message;
      chat.unread_count = unreadCount;
      if (selectedChatId === conversationId) {
        emitter.emit(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS);
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      } else if (unreadCount > 0) {
        // New unread message in a non-selected conversation — debounced sidebar refresh
        debouncedSidebarRefresh();
      }
    }
  },

  [types.ADD_CONVERSATION](_state, conversation) {
    _state.allConversations.push(conversation);
    // New conversation affects unread counts — debounced to avoid excessive refreshes
    debouncedSidebarRefresh();
  },

  [types.DELETE_CONVERSATION](_state, conversationId) {
    _state.allConversations = _state.allConversations.filter(
      c => c.id !== conversationId
    );
  },

  [types.UPDATE_CONVERSATION](_state, conversation) {
    const { allConversations } = _state;
    const index = allConversations.findIndex(c => c.id === conversation.id);

    if (index > -1) {
      const selectedConversation = allConversations[index];

      // ignore out of order events
      if (conversation.updated_at < selectedConversation.updated_at) {
        return;
      }

      const { messages, ...updates } = conversation;

      // Normalize timestamps
      if (updates.last_activity_at) {
        const lastActivitySeconds = Number(updates.last_activity_at);
        if (!Number.isNaN(lastActivitySeconds)) {
          updates.last_activity_at = Math.floor(lastActivitySeconds);
          updates.timestamp = Math.floor(lastActivitySeconds);
        }
      }

      allConversations[index] = { ...selectedConversation, ...updates };
      if (_state.selectedChatId === conversation.id) {
        emitter.emit(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS);
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      }
    } else {
      _state.allConversations.push(conversation);
    }
  },

  [types.SET_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = true;
  },

  [types.CLEAR_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = false;
  },

  [types.UPDATE_MESSAGE_UNREAD_COUNT](
    _state,
    { id, lastSeen, unreadCount = 0 }
  ) {
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (chat) {
      if (lastSeen !== undefined) {
        chat.agent_last_seen_at = lastSeen;
      }
      chat.unread_count = unreadCount;
    }
  },
  [types.CHANGE_CHAT_STATUS_FILTER](_state, data) {
    _state.chatStatusFilter = data;
  },

  [types.CHANGE_CHAT_SORT_FILTER](_state, data) {
    _state.chatSortFilter = data;
  },

  // Update assignee on action cable message
  [types.UPDATE_ASSIGNEE](_state, payload) {
    const [chat] = _state.allConversations.filter(c => c.id === payload.id);
    chat.meta.assignee = payload.assignee;
  },

  [types.UPDATE_CONVERSATION_CONTACT](_state, { conversationId, ...payload }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.meta.sender = payload;
    }
  },

  [types.SET_ACTIVE_INBOX](_state, inboxId) {
    _state.currentInbox = inboxId ? parseInt(inboxId, 10) : null;
  },

  [types.SET_CONVERSATION_CAN_REPLY](_state, { conversationId, canReply }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.can_reply = canReply;
    }
  },

  [types.CLEAR_CONTACT_CONVERSATIONS](_state, contactId) {
    const chats = _state.allConversations.filter(
      c => c.meta.sender.id !== contactId
    );
    _state.allConversations = chats;
  },

  [types.SET_CONVERSATION_FILTERS](_state, data) {
    _state.appliedFilters = data;
  },

  [types.CLEAR_CONVERSATION_FILTERS](_state) {
    _state.appliedFilters = [];
  },

  [types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION](
    _state,
    { conversationId, messageId }
  ) {
    _state.syncConversationsMessages[conversationId] = messageId;
  },

  [types.SET_CONTEXT_MENU_CHAT_ID](_state, chatId) {
    _state.contextMenuChatId = chatId;
  },

  [types.SET_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = data;
  },
  [types.UPDATE_CHAT_LIST_FILTERS](_state, data) {
    // MERGE, do not replace. Callers like ReconnectService dispatch partial
    // updates (e.g. { page: 1, updatedWithin: null }); replacing the whole
    // object wiped assigneeType/status/conversationType/sortBy, so the next
    // fetchAllConversations pulled a wrong, unfiltered dataset and (page===1)
    // replaced the agent's list with it — lists went stale/wrong until a
    // manual refresh. Full-object callers (ChatList) are unaffected: they
    // always pass every key, so merging overwrites all of them anyway.
    _state.conversationFilters = { ..._state.conversationFilters, ...data };
  },
  [types.SET_INBOX_AILAB_ASSISTANT](_state, data) {
    _state.copilotAssistant = data.assistant;
  },

  [types.UPDATE_CONVERSATION_CALL_STATUS](
    _state,
    { conversationId, callStatus }
  ) {
    const chat = getConversationById(_state)(conversationId);
    if (!chat) return;

    chat.additional_attributes = {
      ...chat.additional_attributes,
      call_status: callStatus,
    };
  },

  [types.UPDATE_MESSAGE_CALL_STATUS](_state, { conversationId, callStatus }) {
    const chat = getConversationById(_state)(conversationId);
    if (!chat) return;

    const lastCall = (chat.messages || []).findLast(
      m => m.content_type === CONTENT_TYPES.VOICE_CALL
    );

    if (!lastCall) return;

    lastCall.content_attributes ??= {};
    lastCall.content_attributes.data = {
      ...lastCall.content_attributes.data,
      status: callStatus,
    };
  },

  [types.UPDATE_CONVERSATION_LABELS_ON_LIST](
    _state,
    { conversationId, labels }
  ) {
    const index = _state.allConversations.findIndex(
      c => c.id === conversationId
    );
    if (index > -1) {
      const existing = _state.allConversations[index];
      _state.allConversations[index] = { ...existing, labels };
    }
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
